import moment from 'moment';
import 'moment/locale/es';

/**
 * Formats timestamp based on the acceptance criteria:
 * - Same day → HH:MM
 * - Previous day → "Yesterday"
 * - Latest 7 days → Day of week
 * - More than 7 days ago → Month Day
 *
 * @param {string|Date} timestamp - The notification timestamp
 * @param {string} locale - The locale for formatting (default: 'en')
 * @returns {string} Formatted timestamp string
 */
export const formatNotificationTimestamp = (timestamp, locale = 'en') => {
  if (!timestamp) return '';

  const notificationDate = moment(timestamp).locale(locale);

  // Check if date is valid
  if (!notificationDate.isValid()) {
    return '';
  }

  // Reset time to start of day for comparison
  const today = moment().startOf('day');
  const notifDay = notificationDate.clone().startOf('day');

  // Calculate difference in days
  const diffDays = today.diff(notifDay, 'days');

  // Same day → HH:MM
  if (diffDays === 0) {
    return notificationDate.format('HH:mm');
  }

  // Previous day → "Yesterday"
  if (diffDays === 1) {
    // Use moment's locale for "Yesterday" translation
    // calendar() without arguments compares to now and uses locale translations
    return notificationDate.calendar(null, {
      lastDay: '[Yesterday]'
    });
  }

  // Latest 7 days → Day of week
  if (diffDays > 1 && diffDays <= 7) {
    return notificationDate.format('dddd');
  }

  // More than 7 days ago → Month Day
  return notificationDate.format('MMM D');
};

/**
 * Formats user name based on sender type:
 * - If sender is admin: just the name
 * - If sender is creator: just the name
 * - If sender is from a brand: sender name + brand name
 *
 * @param {Object} notification - The notification object
 * @param {string} notification.senderName - The sender's name
 * @param {string} notification.senderType - The sender type ('admin', 'creator', 'brand')
 * @param {string} notification.brandName - The brand name (required if senderType is 'brand')
 * @returns {string} Formatted user name
 */
export const getSenderInfo = (notification) => {
  const { subType, sentByType, sentByEmployee, sentByClient, sentByCreator } = notification || {};

  if (subType === 'task.expiring' || (!sentByEmployee && !sentByCreator && !sentByClient)) {
    return null;
  }

  switch (sentByType) {
    case 'employee':
      return { name: sentByEmployee.fullName, image: sentByEmployee.profilePicture };
    case 'client':
      return { name: sentByClient.title, image: sentByClient.logo };
    case 'creator':
      return { name: sentByCreator.fullName, image: sentByCreator.profilePicture };
    default:
      return null;
  }
};
